import {
  Autocomplete,
  Skeleton,
  SxProps,
  TextField,
} from '@mui/material';
import './style.scss';
import { SelectItem } from '../../core/types/util/selectItem';

/** Input method for selecting a single item in a list */
export const SearchSelect = <T extends string|number = string|number>({
  label = 'Søk',
  errorLabel = 'Dette feltet er påkrevd',
  value = null,
  onChange = () => null,
  onClose = () => null,
  size = 'medium',
  children = [],
  required = false,
  forceError = false,
  disabled = false,
  loading = false,
  sx = {},
  inputSx = {},
}: {
  label?: string,
  errorLabel?: string,
  value?: SelectItem<T>|null,
  onChange?: (newValue: SelectItem<T>|undefined) => void,
  onClose?: () => void,
  size?: 'medium' | 'small',
  children?: SelectItem<T>[],
  required?: boolean,
  forceError?: boolean,
  disabled?: boolean,
  loading?: boolean,
  sx?: SxProps,
  inputSx?: SxProps,
}) => {
  if (loading) {
    return (
      <Skeleton
        width="100%"
        height={`${size === 'medium' ? 56 : 40}px`}
        variant="rectangular"
      />
    );
  }

  return (
    <Autocomplete
      className="search-select-component"
      value={value}
      disabled={disabled}
      isOptionEqualToValue={(a, b) => `${a.id}` === `${b.id}`}
      clearOnBlur
      clearOnEscape
      openOnFocus
      getOptionDisabled={(opt) => opt.disabled === true}
      onChange={(e, newValue) => {
        onChange(newValue || undefined);
      }}
      onClose={onClose}
      options={children}
      fullWidth
      getOptionLabel={(option) => option?.label}
      sx={sx}
      renderInput={(params) => (
        <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          sx={inputSx}
          label={label}
          helperText={(required && !value) || forceError ? errorLabel : null}
          error={(required && !value) || forceError}
        />
      )}
      size={size}
    />
  );
};

/**
 * Same as a search select, but uses a string or number for value and onChange
 * directly instead of using SelectItems
 */
export const SearchSelectDirect = <T extends string|number>({
  label = 'Søk',
  errorLabel = 'Dette feltet er påkrevd',
  value,
  notFoundLabel,
  onChange = () => null,
  onClose = () => null,
  size = 'medium',
  children = [],
  required = false,
  forceError = false,
  disabled = false,
  loading = false,
  sx = {},
  inputSx = {},
}: {
  label?: string,
  errorLabel?: string,
  value?: T|null,
  notFoundLabel?: string|undefined,
  onChange?: (newValue: T|undefined) => void,
  onClose?: () => void,
  size?: 'medium' | 'small',
  children?: SelectItem<T>[],
  required?: boolean,
  forceError?: boolean,
  disabled?: boolean,
  loading?: boolean,
  sx?: SxProps,
  inputSx?: SxProps,
}) => {
  const change = (v: SelectItem<T>|undefined) => onChange(v?.id);
  const val = value !== null && value !== undefined
    ? children.find((c) => c.id === value)
      || { id: value, label: notFoundLabel ?? `${value}` }
    : null;
  return (
    <SearchSelect
      label={label}
      errorLabel={errorLabel}
      value={val}
      onChange={change}
      onClose={onClose}
      size={size}
      required={required}
      forceError={forceError}
      disabled={disabled}
      loading={loading}
      sx={sx}
      inputSx={inputSx}
    >
      {children}
    </SearchSelect>
  );
};
